/**
 * Système de thème robuste et extensible
 * 
 * Pour ajouter un nouveau thème :
 * 1. Ajoutez une nouvelle clé dans ThemeConfig
 * 2. Définissez toutes les couleurs nécessaires
 * 3. Le thème sera automatiquement disponible dans toute l'application
 */

export type ThemeName = 'light' | 'dark' | 'blue' | 'purple' | 'green';

export interface ThemeColors {
  // Navigation
  navigation: {
    bg: string;
    border: string;
    text: string;
    textHover: string;
    icon: string;
    iconHover: string;
  };
  
  // Layout
  layout: {
    bg: string;
    sidebar: {
      bg: string;
      border: string;
      text: string;
      textHover: string;
      textActive: string;
      icon: string;
      iconActive: string;
      sectionLabel: string;
    };
  };
  
  // Cards & Surfaces
  surface: {
    card: {
      bg: string;
      border: string;
      shadow: string;
    };
    subtle: {
      bg: string;
    };
  };
  
  // Typography
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    disabled: string;
    inverse: string;
  };
  
  // Buttons
  button: {
    primary: {
      bg: string;
      text: string;
      hover: string;
      active: string;
      border: string;
    };
    secondary: {
      bg: string;
      text: string;
      hover: string;
      active: string;
      border: string;
    };
    outline: {
      bg: string;
      text: string;
      hover: string;
      active: string;
      border: string;
    };
    ghost: {
      bg: string;
      text: string;
      hover: string;
      active: string;
    };
  };
  
  // Links
  link: {
    default: string;
    hover: string;
    active: string;
    visited: string;
  };
  
  // Inputs & Forms
  input: {
    bg: string;
    border: string;
    borderFocus: string;
    text: string;
    placeholder: string;
    disabled: {
      bg: string;
      text: string;
    };
  };
  
  // Status colors
  status: {
    success: {
      bg: string;
      text: string;
      border: string;
    };
    warning: {
      bg: string;
      text: string;
      border: string;
    };
    error: {
      bg: string;
      text: string;
      border: string;
    };
    info: {
      bg: string;
      text: string;
      border: string;
    };
  };
  
  // Hover states
  hover: {
    card: string;
    button: string;
    link: string;
  };
}

export const themes: Record<ThemeName, ThemeColors> = {
  // Thème Light (Shopify blanc)
  light: {
    navigation: {
      bg: '#1e1e1e',
      border: '#3e3e42',
      text: '#cccccc',
      textHover: '#ffffff',
      icon: '#cccccc',
      iconHover: '#ffffff',
    },
    layout: {
      bg: '#f6f6f7',
      sidebar: {
        bg: '#ffffff',
        border: '#e5e7eb',
        text: '#6b7280',
        textHover: '#1a1a1a',
        textActive: '#1a1a1a',
        icon: '#6b7280',
        iconActive: '#1a1a1a',
        sectionLabel: '#6b7280',
      },
    },
    surface: {
      card: {
        bg: '#ffffff',
        border: '#e5e7eb',
        shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      },
      subtle: {
        bg: '#ffffff',
      },
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#6b7280',
      tertiary: '#9ca3af',
      disabled: '#d1d5db',
      inverse: '#ffffff',
    },
    button: {
      primary: {
        bg: '#1a1a1a',
        text: '#ffffff',
        hover: '#2a2a2a',
        active: '#0a0a0a',
        border: '#1a1a1a',
      },
      secondary: {
        bg: '#f3f4f6',
        text: '#1a1a1a',
        hover: '#e5e7eb',
        active: '#d1d5db',
        border: '#e5e7eb',
      },
      outline: {
        bg: 'transparent',
        text: '#1a1a1a',
        hover: '#f3f4f6',
        active: '#e5e7eb',
        border: '#e5e7eb',
      },
      ghost: {
        bg: 'transparent',
        text: '#6b7280',
        hover: '#f3f4f6',
        active: '#e5e7eb',
      },
    },
    link: {
      default: '#1a1a1a',
      hover: '#2a2a2a',
      active: '#0a0a0a',
      visited: '#4b5563',
    },
    input: {
      bg: '#ffffff',
      border: '#e5e7eb',
      borderFocus: '#1a1a1a',
      text: '#1a1a1a',
      placeholder: '#9ca3af',
      disabled: {
        bg: '#f9fafb',
        text: '#9ca3af',
      },
    },
    status: {
      success: {
        bg: '#d1fae5',
        text: '#065f46',
        border: '#10b981',
      },
      warning: {
        bg: '#fef3c7',
        text: '#92400e',
        border: '#f59e0b',
      },
      error: {
        bg: '#fee2e2',
        text: '#991b1b',
        border: '#ef4444',
      },
      info: {
        bg: '#dbeafe',
        text: '#1e40af',
        border: '#3b82f6',
      },
    },
    hover: {
      card: '#f9fafb',
      button: '#f3f4f6',
      link: '#f3f4f6',
    },
  },
  
  // Thème Dark (Cursor IDE - couleurs exactes)
  dark: {
    navigation: {
      bg: '#1e1e1e',
      border: '#3e3e42',
      text: '#cccccc',
      textHover: '#ffffff',
      icon: '#cccccc',
      iconHover: '#ffffff',
    },
    layout: {
      bg: '#1e1e1e',
      sidebar: {
        bg: '#252526',
        border: '#3e3e42',
        text: '#cccccc',
        textHover: '#ffffff',
        textActive: '#ffffff',
        icon: '#cccccc',
        iconActive: '#8B5CF6',
        sectionLabel: '#858585',
      },
    },
    surface: {
      card: {
        bg: '#252526',
        border: '#3e3e42',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
      },
      subtle: {
        bg: '#2a2d2e',
      },
    },
    text: {
      primary: '#cccccc',
      secondary: '#858585',
      tertiary: '#6a6a6a',
      disabled: '#4a4a4a',
      inverse: '#1e1e1e',
    },
    button: {
      primary: {
        bg: '#ffffff',
        text: '#1e1e1e',
        hover: '#e5e7eb',
        active: '#d1d5db',
        border: '#ffffff',
      },
      secondary: {
        bg: '#ffffff',
        text: '#1e1e1e',
        hover: '#f3f4f6',
        active: '#e5e7eb',
        border: '#d1d5db',
      },
      outline: {
        bg: 'transparent',
        text: '#cccccc',
        hover: '#3e3e42',
        active: '#454545',
        border: '#3e3e42',
      },
      ghost: {
        bg: 'transparent',
        text: '#cccccc',
        hover: '#2a2d2e',
        active: '#3e3e42',
      },
    },
    link: {
      default: '#8B5CF6',
      hover: '#A78BFA',
      active: '#7C3AED',
      visited: '#A78BFA',
    },
    input: {
      bg: '#3c3c3c',
      border: '#3e3e42',
      borderFocus: '#8B5CF6',
      text: '#cccccc',
      placeholder: '#6a6a6a',
      disabled: {
        bg: '#2a2d2e',
        text: '#4a4a4a',
      },
    },
    status: {
      success: {
        bg: '#0e4a1f',
        text: '#4ec9b0',
        border: '#4ec9b0',
      },
      warning: {
        bg: '#5a4d1f',
        text: '#dcdcaa',
        border: '#dcdcaa',
      },
      error: {
        bg: '#5a1d1d',
        text: '#f48771',
        border: '#f48771',
      },
      info: {
        bg: '#094771',
        text: '#4ec9b0',
        border: '#4ec9b0',
      },
    },
    hover: {
      card: '#2a2d2e',
      button: '#3e3e42',
      link: '#2a2d2e',
    },
  },
  
  // Thème Blue (exemple pour démontrer l'extensibilité)
  blue: {
    navigation: {
      bg: '#1e3a8a',
      border: '#1e40af',
      text: '#ffffff',
      textHover: '#ffffff',
      icon: '#ffffff',
      iconHover: '#ffffff',
    },
    layout: {
      bg: '#f0f9ff',
      sidebar: {
        bg: '#ffffff',
        border: '#e0f2fe',
        text: '#64748b',
        textHover: '#1e3a8a',
        textActive: '#1e3a8a',
        icon: '#64748b',
        iconActive: '#1e3a8a',
        sectionLabel: '#64748b',
      },
    },
    surface: {
      card: {
        bg: '#ffffff',
        border: '#e0f2fe',
        shadow: '0 1px 3px 0 rgba(30, 58, 138, 0.1), 0 1px 2px 0 rgba(30, 58, 138, 0.06)',
      },
      subtle: {
        bg: '#ffffff',
      },
    },
    text: {
      primary: '#1e3a8a',
      secondary: '#64748b',
      tertiary: '#94a3b8',
      disabled: '#cbd5e1',
      inverse: '#ffffff',
    },
    button: {
      primary: {
        bg: '#1e3a8a',
        text: '#ffffff',
        hover: '#1e40af',
        active: '#1e3a8a',
        border: '#1e3a8a',
      },
      secondary: {
        bg: '#eff6ff',
        text: '#1e3a8a',
        hover: '#dbeafe',
        active: '#bfdbfe',
        border: '#bfdbfe',
      },
      outline: {
        bg: 'transparent',
        text: '#1e3a8a',
        hover: '#eff6ff',
        active: '#dbeafe',
        border: '#1e3a8a',
      },
      ghost: {
        bg: 'transparent',
        text: '#64748b',
        hover: '#eff6ff',
        active: '#dbeafe',
      },
    },
    link: {
      default: '#1e3a8a',
      hover: '#1e40af',
      active: '#1e3a8a',
      visited: '#3b82f6',
    },
    input: {
      bg: '#ffffff',
      border: '#e0f2fe',
      borderFocus: '#1e3a8a',
      text: '#1e3a8a',
      placeholder: '#94a3b8',
      disabled: {
        bg: '#f8fafc',
        text: '#94a3b8',
      },
    },
    status: {
      success: {
        bg: '#d1fae5',
        text: '#065f46',
        border: '#10b981',
      },
      warning: {
        bg: '#fef3c7',
        text: '#92400e',
        border: '#f59e0b',
      },
      error: {
        bg: '#fee2e2',
        text: '#991b1b',
        border: '#ef4444',
      },
      info: {
        bg: '#dbeafe',
        text: '#1e40af',
        border: '#3b82f6',
      },
    },
    hover: {
      card: '#f0f9ff',
      button: '#eff6ff',
      link: '#eff6ff',
    },
  },
  
  // Thème Purple (exemple)
  purple: {
    navigation: {
      bg: '#6b21a8',
      border: '#7c3aed',
      text: '#ffffff',
      textHover: '#ffffff',
      icon: '#ffffff',
      iconHover: '#ffffff',
    },
    layout: {
      bg: '#faf5ff',
      sidebar: {
        bg: '#ffffff',
        border: '#f3e8ff',
        text: '#6b7280',
        textHover: '#6b21a8',
        textActive: '#6b21a8',
        icon: '#6b7280',
        iconActive: '#6b21a8',
        sectionLabel: '#6b7280',
      },
    },
    surface: {
      card: {
        bg: '#ffffff',
        border: '#f3e8ff',
        shadow: '0 1px 3px 0 rgba(107, 33, 168, 0.1), 0 1px 2px 0 rgba(107, 33, 168, 0.06)',
      },
      subtle: {
        bg: '#ffffff',
      },
    },
    text: {
      primary: '#6b21a8',
      secondary: '#6b7280',
      tertiary: '#9ca3af',
      disabled: '#d1d5db',
      inverse: '#ffffff',
    },
    button: {
      primary: {
        bg: '#6b21a8',
        text: '#ffffff',
        hover: '#7c3aed',
        active: '#5b21b6',
        border: '#6b21a8',
      },
      secondary: {
        bg: '#faf5ff',
        text: '#6b21a8',
        hover: '#f3e8ff',
        active: '#e9d5ff',
        border: '#e9d5ff',
      },
      outline: {
        bg: 'transparent',
        text: '#6b21a8',
        hover: '#faf5ff',
        active: '#f3e8ff',
        border: '#6b21a8',
      },
      ghost: {
        bg: 'transparent',
        text: '#6b7280',
        hover: '#faf5ff',
        active: '#f3e8ff',
      },
    },
    link: {
      default: '#6b21a8',
      hover: '#7c3aed',
      active: '#5b21b6',
      visited: '#8b5cf6',
    },
    input: {
      bg: '#ffffff',
      border: '#f3e8ff',
      borderFocus: '#6b21a8',
      text: '#6b21a8',
      placeholder: '#9ca3af',
      disabled: {
        bg: '#faf5ff',
        text: '#9ca3af',
      },
    },
    status: {
      success: {
        bg: '#d1fae5',
        text: '#065f46',
        border: '#10b981',
      },
      warning: {
        bg: '#fef3c7',
        text: '#92400e',
        border: '#f59e0b',
      },
      error: {
        bg: '#fee2e2',
        text: '#991b1b',
        border: '#ef4444',
      },
      info: {
        bg: '#dbeafe',
        text: '#1e40af',
        border: '#3b82f6',
      },
    },
    hover: {
      card: '#faf5ff',
      button: '#f3e8ff',
      link: '#f3e8ff',
    },
  },
  
  // Thème Green (exemple)
  green: {
    navigation: {
      bg: '#065f46',
      border: '#047857',
      text: '#ffffff',
      textHover: '#ffffff',
      icon: '#ffffff',
      iconHover: '#ffffff',
    },
    layout: {
      bg: '#f0fdf4',
      sidebar: {
        bg: '#ffffff',
        border: '#dcfce7',
        text: '#6b7280',
        textHover: '#065f46',
        textActive: '#065f46',
        icon: '#6b7280',
        iconActive: '#065f46',
        sectionLabel: '#6b7280',
      },
    },
    surface: {
      card: {
        bg: '#ffffff',
        border: '#dcfce7',
        shadow: '0 1px 3px 0 rgba(6, 95, 70, 0.1), 0 1px 2px 0 rgba(6, 95, 70, 0.06)',
      },
      subtle: {
        bg: '#ffffff',
      },
    },
    text: {
      primary: '#065f46',
      secondary: '#6b7280',
      tertiary: '#9ca3af',
      disabled: '#d1d5db',
      inverse: '#ffffff',
    },
    button: {
      primary: {
        bg: '#065f46',
        text: '#ffffff',
        hover: '#047857',
        active: '#064e3b',
        border: '#065f46',
      },
      secondary: {
        bg: '#f0fdf4',
        text: '#065f46',
        hover: '#dcfce7',
        active: '#bbf7d0',
        border: '#bbf7d0',
      },
      outline: {
        bg: 'transparent',
        text: '#065f46',
        hover: '#f0fdf4',
        active: '#dcfce7',
        border: '#065f46',
      },
      ghost: {
        bg: 'transparent',
        text: '#6b7280',
        hover: '#f0fdf4',
        active: '#dcfce7',
      },
    },
    link: {
      default: '#065f46',
      hover: '#047857',
      active: '#064e3b',
      visited: '#10b981',
    },
    input: {
      bg: '#ffffff',
      border: '#dcfce7',
      borderFocus: '#065f46',
      text: '#065f46',
      placeholder: '#9ca3af',
      disabled: {
        bg: '#f0fdf4',
        text: '#9ca3af',
      },
    },
    status: {
      success: {
        bg: '#d1fae5',
        text: '#065f46',
        border: '#10b981',
      },
      warning: {
        bg: '#fef3c7',
        text: '#92400e',
        border: '#f59e0b',
      },
      error: {
        bg: '#fee2e2',
        text: '#991b1b',
        border: '#ef4444',
      },
      info: {
        bg: '#dbeafe',
        text: '#1e40af',
        border: '#3b82f6',
      },
    },
    hover: {
      card: '#f0fdf4',
      button: '#dcfce7',
      link: '#dcfce7',
    },
  },
};

/**
 * Obtient le thème actuel
 */
export const getTheme = (themeName: ThemeName = 'light'): ThemeColors => {
  return themes[themeName] || themes.light;
};

const toRgbTriplet = (value: string): string | null => {
  if (!value) return null;
  const color = value.trim();

  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map((char) => char + char).join('');
    }

    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
        return `${r} ${g} ${b}`;
      }
    }
    return null;
  }

  const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgbMatch) {
    return `${rgbMatch[1]} ${rgbMatch[2]} ${rgbMatch[3]}`;
  }

  return null;
};

const setRgbVariable = (root: HTMLElement, name: string, value: string | undefined) => {
  if (!value) return;
  const rgb = toRgbTriplet(value);
  if (rgb) {
    root.style.setProperty(name, rgb);
  }
};

/**
 * Applique les variables CSS pour le thème
 */
export const applyThemeToDocument = (theme: ThemeColors) => {
  if (typeof window === 'undefined' || !document || !document.documentElement) {
    return;
  }
  
  const root = document.documentElement;
  
  // Navigation
  root.style.setProperty('--theme-nav-bg', theme.navigation.bg);
  root.style.setProperty('--theme-nav-border', theme.navigation.border);
  root.style.setProperty('--theme-nav-text', theme.navigation.text);
  root.style.setProperty('--theme-nav-text-hover', theme.navigation.textHover);
  root.style.setProperty('--theme-nav-icon', theme.navigation.icon);
  root.style.setProperty('--theme-nav-icon-hover', theme.navigation.iconHover);
  
  // Layout
  root.style.setProperty('--theme-layout-bg', theme.layout.bg);
  root.style.setProperty('--theme-sidebar-bg', theme.layout.sidebar.bg);
  root.style.setProperty('--theme-sidebar-border', theme.layout.sidebar.border);
  root.style.setProperty('--theme-sidebar-text', theme.layout.sidebar.text);
  root.style.setProperty('--theme-sidebar-text-hover', theme.layout.sidebar.textHover);
  root.style.setProperty('--theme-sidebar-text-active', theme.layout.sidebar.textActive);
  root.style.setProperty('--theme-sidebar-icon', theme.layout.sidebar.icon);
  root.style.setProperty('--theme-sidebar-icon-active', theme.layout.sidebar.iconActive);
  root.style.setProperty('--theme-sidebar-section-label', theme.layout.sidebar.sectionLabel);
  
  // Surface
  root.style.setProperty('--theme-card-bg', theme.surface.card.bg);
  root.style.setProperty('--theme-card-border', theme.surface.card.border);
  root.style.setProperty('--theme-card-shadow', theme.surface.card.shadow);
  root.style.setProperty('--theme-subtle-bg', theme.surface.subtle.bg);
  
  // Text
  root.style.setProperty('--theme-text-primary', theme.text.primary);
  root.style.setProperty('--theme-text-secondary', theme.text.secondary);
  root.style.setProperty('--theme-text-tertiary', theme.text.tertiary);
  root.style.setProperty('--theme-text-disabled', theme.text.disabled);
  root.style.setProperty('--theme-text-inverse', theme.text.inverse);
  
  // Buttons
  root.style.setProperty('--theme-btn-primary-bg', theme.button.primary.bg);
  root.style.setProperty('--theme-btn-primary-text', theme.button.primary.text);
  root.style.setProperty('--theme-btn-primary-hover', theme.button.primary.hover);
  root.style.setProperty('--theme-btn-primary-active', theme.button.primary.active);
  root.style.setProperty('--theme-btn-primary-border', theme.button.primary.border);
  
  root.style.setProperty('--theme-btn-secondary-bg', theme.button.secondary.bg);
  root.style.setProperty('--theme-btn-secondary-text', theme.button.secondary.text);
  root.style.setProperty('--theme-btn-secondary-hover', theme.button.secondary.hover);
  root.style.setProperty('--theme-btn-secondary-active', theme.button.secondary.active);
  root.style.setProperty('--theme-btn-secondary-border', theme.button.secondary.border);
  
  root.style.setProperty('--theme-btn-outline-bg', theme.button.outline.bg);
  root.style.setProperty('--theme-btn-outline-text', theme.button.outline.text);
  root.style.setProperty('--theme-btn-outline-hover', theme.button.outline.hover);
  root.style.setProperty('--theme-btn-outline-active', theme.button.outline.active);
  root.style.setProperty('--theme-btn-outline-border', theme.button.outline.border);
  
  root.style.setProperty('--theme-btn-ghost-bg', theme.button.ghost.bg);
  root.style.setProperty('--theme-btn-ghost-text', theme.button.ghost.text);
  root.style.setProperty('--theme-btn-ghost-hover', theme.button.ghost.hover);
  root.style.setProperty('--theme-btn-ghost-active', theme.button.ghost.active);
  
  // Links
  root.style.setProperty('--theme-link-default', theme.link.default);
  root.style.setProperty('--theme-link-hover', theme.link.hover);
  root.style.setProperty('--theme-link-active', theme.link.active);
  root.style.setProperty('--theme-link-visited', theme.link.visited);
  
  // Inputs
  root.style.setProperty('--theme-input-bg', theme.input.bg);
  root.style.setProperty('--theme-input-border', theme.input.border);
  root.style.setProperty('--theme-input-border-focus', theme.input.borderFocus);
  root.style.setProperty('--theme-input-text', theme.input.text);
  root.style.setProperty('--theme-input-placeholder', theme.input.placeholder);
  root.style.setProperty('--theme-input-disabled-bg', theme.input.disabled.bg);
  root.style.setProperty('--theme-input-disabled-text', theme.input.disabled.text);
  
  // Status
  root.style.setProperty('--theme-status-success-bg', theme.status.success.bg);
  root.style.setProperty('--theme-status-success-text', theme.status.success.text);
  root.style.setProperty('--theme-status-success-border', theme.status.success.border);
  
  root.style.setProperty('--theme-status-warning-bg', theme.status.warning.bg);
  root.style.setProperty('--theme-status-warning-text', theme.status.warning.text);
  root.style.setProperty('--theme-status-warning-border', theme.status.warning.border);
  
  root.style.setProperty('--theme-status-error-bg', theme.status.error.bg);
  root.style.setProperty('--theme-status-error-text', theme.status.error.text);
  root.style.setProperty('--theme-status-error-border', theme.status.error.border);
  
  root.style.setProperty('--theme-status-info-bg', theme.status.info.bg);
  root.style.setProperty('--theme-status-info-text', theme.status.info.text);
  root.style.setProperty('--theme-status-info-border', theme.status.info.border);
  
  // Hover
  root.style.setProperty('--theme-hover-card', theme.hover.card);
  root.style.setProperty('--theme-hover-button', theme.hover.button);
  root.style.setProperty('--theme-hover-link', theme.hover.link);

  // Global tokens (RGB triplets) used across layout
  setRgbVariable(root, '--bg', theme.layout.bg);
  setRgbVariable(root, '--card', theme.surface.card.bg);
  setRgbVariable(root, '--elevated', theme.surface.card.bg);
  setRgbVariable(root, '--subtle', theme.surface.subtle.bg);
  setRgbVariable(root, '--fg', theme.text.primary);
  setRgbVariable(root, '--muted', theme.text.secondary);
  setRgbVariable(root, '--muted-2', theme.text.tertiary);
  setRgbVariable(root, '--border', theme.surface.card.border);
  setRgbVariable(root, '--border-strong', theme.layout.sidebar.border);
  setRgbVariable(root, '--theme-nav-bg-rgb', theme.navigation.bg);
  setRgbVariable(root, '--theme-layout-bg-rgb', theme.layout.bg);
  setRgbVariable(root, '--theme-sidebar-bg-rgb', theme.layout.sidebar.bg);
  setRgbVariable(root, '--theme-card-bg-rgb', theme.surface.card.bg);
};

